'use client'

import { useCallback, useMemo } from 'react'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
    DropdownMenuGroup,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { useState } from 'react'
import { useOrganization, useOrganizationList, useUser } from '@clerk/nextjs'
import { toast } from '@/features/toast/toast'
import { Skeleton } from '@/components/ui/skeleton'
import Image from 'next/image'
import { useRouter, usePathname } from 'next/navigation'
import { cn } from '@/utils/styles/cn'
import { IOrganization, useOrganizationsQuery } from '@/api/user/organization/get-organizations'
import {
    ArrowLeft,
    ArrowUpCircle,
    BookOpen,
    Check,
    ChevronsUpDown,
    Gift,
    Headset,
    LogOut,
    LucideIcon,
    Newspaper,
    Plus,
    Receipt,
    Search,
    Settings,
    ShieldCheck,
    Sparkles,
    Trash,
    Users,
} from 'lucide-react'
import { ActionBarDropdownCredits } from '@/features/action-bar/items/action-bar-dropdown/action-bar-dropdown-credits'
import { Route, UrlConfig } from '@/setup/routing/routing'
import { useBrandRoute } from '@/setup/routing/use-brand-router'
import { useIsSuperUser } from '@/utils/clerk/use-is-super-user'
import { useModalContext } from '@/features/modal/model-context'
import { createImageUrl } from '@/utils/image/create-image-url'
import { useSignOut } from '@/hooks/use-sign-out'
import { useScreenContext } from '@/features/layout/screen-context'
import { useSidebarContext } from '@/features/sidebar/sidebar-context'
import { useSettingsModal } from '@/features/settings-modal/settings-modal-context'
import { useCreatePortalSessionMutation } from '@/api/user/organization/billing/create-portal-session-mutation'
import { useOrganizationId } from '@/features/organization/use-organization-id'
import { Input } from '@/components/ui/input'

import { useTutorialCenterModal } from '@/features/tutorial-center/tutorial-center-modal-context'
import { useReferralModal } from '@/features/referral/referral-modal-context'
import { useAccessControl } from '@/features/access-control/use-access-control'
import { ManagerAccessControlRule } from '@/features/access-control/access-control-rules'
import { useEnterManagerMutation } from '@/api/agents/mutations/enter-manager-mutation'
import { useBrandContext } from '@/features/brands/brand-context'
import { useCurrentBrandGuard } from '@/features/brands/use-current-brand-guard'
import { isManagerRoute } from '@/features/layout/is-manager-route'
import { useWafCaptchaTestAction } from '@/features/waf-captcha/use-waf-captcha-test-action'
import { useGetBrandsQuery } from '@/api/brands/queries/get-brands-query'
import { IBrand } from '@/api/brands/queries/get-brand-query'
import { useBillingFeatureLimit } from '@/features/subscription/utils/billing-helpers'
import { useCreateBrandFlowModal } from '@/features/resources/implementations/create/create-brand-flow-modal-context'
import { DeleteBrandConfirmationModal } from '@/features/resources/implementations/overview/sections/brands-list-section/delete-brand-confirmation-modal'
import { useDeleteBrandFlow } from '@/features/brands/use-delete-brand-flow'
import { useVerticalScrollFade } from '@/hooks/use-vertical-scroll-fade'

type IDropdownItemsConfig = {
    credits: boolean
    organizationSwitcher: boolean
    settings: boolean
    roadmap: boolean
    billing: boolean
    inviteTeam: boolean
    referral: boolean
    invoices: boolean
    tutorials: boolean
    productNews: boolean
    signOut: boolean
}

const DEFAULT_DROPDOWN_ITEMS_CONFIG: IDropdownItemsConfig = {
    credits: false,
    settings: false,
    roadmap: false,
    billing: false,
    inviteTeam: false,
    referral: false,
    invoices: false,
    tutorials: false,
    productNews: false,
    organizationSwitcher: true,
    signOut: true,
}

const MOBILE_DROPDOWN_ITEMS_CONFIG: IDropdownItemsConfig = {
    credits: false,
    settings: false,
    roadmap: false,
    billing: false,
    inviteTeam: false,
    referral: false,
    invoices: false,
    tutorials: false,
    productNews: false,
    organizationSwitcher: true,
    signOut: true,
}

/**
 * `cell` is the trigger's leading avatar box. At `lg` it is fixed rather than shrink-wrapped,
 * because the collapsed sidebar rail centres the whole trigger inside a 32px cell while the
 * expanded one left-aligns it: holding the box at 32px is what keeps the avatar's centre in the
 * same place when the sidebar toggles, whatever size the avatar inside it is.
 */
const TRIGGER_SIZE = {
    default: { wrapper: 'h-[34px]', cell: '', monogram: 'md' },
    lg: { wrapper: 'h-[42px]', cell: 'size-8', monogram: 'sm' },
} as const

/** Rendered avatar box (`size-6`); `createImageUrl` doubles it for retina. */
const TRIGGER_AVATAR_SIZE = 24

/** Distinct, high-contrast monogram colors used until brands expose a dedicated selector logo. */
const BRAND_MONOGRAM_COLORS = ['#F05A1A', '#1D4ED8', '#047857', '#7C3AED', '#BE123C', '#334155'] as const

const EMPTY_BRAND_CREATE_TOOLTIP = 'Finish setting up this brand first or delete it to create a new brand'

/** Item count above which account-menu switcher sections reveal search and scrolling. */
const SWITCHER_OVERFLOW_THRESHOLD = 5

function getBrandMonogramColor(name: string) {
    const hash = Array.from(name).reduce((sum, character) => sum + character.charCodeAt(0), 0)
    return BRAND_MONOGRAM_COLORS[hash % BRAND_MONOGRAM_COLORS.length]
}

function BrandMonogram({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' }) {
    const initial = name.trim().slice(0, 1).toUpperCase() || 'B'

    return (
        <span
            aria-hidden="true"
            className={cn(
                'flex shrink-0 items-center justify-center font-medium text-white',
                size === 'sm' && 'size-6 rounded-md text-[12px]',
                size === 'md' && 'size-7 rounded-lg text-[13px]'
            )}
            style={{ backgroundColor: getBrandMonogramColor(name) }}
        >
            {initial}
        </span>
    )
}

function AccountMenuSectionSearch({
    ariaLabel,
    placeholder,
    value,
    onValueChange,
}: {
    ariaLabel: string
    placeholder: string
    value: string
    onValueChange: (value: string) => void
}) {
    return (
        <div className="px-2 pb-1">
            <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-white/40" />
                <Input
                    aria-label={ariaLabel}
                    placeholder={placeholder}
                    value={value}
                    onValueChange={onValueChange}
                    onKeyDown={(event) => event.stopPropagation()}
                    className={cn(
                        'h-8 rounded-md border-white/10 bg-black/20 pl-8 pr-2 text-[13px] text-white',
                        'placeholder:text-white/35 focus-visible:border-white/20 focus-visible:bg-black/25'
                    )}
                />
            </div>
        </div>
    )
}

function AccountMenuSwitcherList({ ariaLabel, children }: { ariaLabel: string; children: React.ReactNode }) {
    const { scrollRef, updateScrollState, hasScrolledDown, canScrollDown } = useVerticalScrollFade()

    return (
        <div className="relative">
            <div
                aria-hidden="true"
                className={cn(
                    'pointer-events-none absolute inset-x-0 top-0 z-10 h-12',
                    'bg-[linear-gradient(to_bottom,#27272A_0%,rgba(39,39,42,0.96)_35%,rgba(39,39,42,0.55)_70%,rgba(39,39,42,0)_100%)]',
                    'transition-opacity duration-150 ease-[cubic-bezier(0.2,0,0,1)]',
                    hasScrolledDown ? 'opacity-100' : 'opacity-0'
                )}
            />
            <div
                ref={scrollRef}
                aria-label={ariaLabel}
                onScroll={updateScrollState}
                className="no-scrollbar max-h-[200px] touch-pan-y overflow-y-auto overscroll-contain"
            >
                {children}
            </div>
            <div
                aria-hidden="true"
                className={cn(
                    'pointer-events-none absolute inset-x-0 bottom-0 z-10 h-8',
                    'bg-[linear-gradient(to_top,#27272A_0%,rgba(39,39,42,0.96)_35%,rgba(39,39,42,0.55)_70%,rgba(39,39,42,0)_100%)]',
                    'transition-opacity duration-150 ease-[cubic-bezier(0.2,0,0,1)]',
                    canScrollDown ? 'opacity-100' : 'opacity-0'
                )}
            />
        </div>
    )
}

export function ActionBarDropdown({
    config: configOverrides = {},
    hideChevron = false,
    size = 'default',
    menuSide = 'top',
    menuAlign = 'start',
    triggerClassName,
}: {
    config?: Partial<IDropdownItemsConfig>
    hideChevron?: boolean
    size?: 'default' | 'lg'
    /** Side the menu opens toward. Defaults to `top` (sidebar placement); use `bottom` in a top navbar. */
    menuSide?: 'top' | 'bottom'
    /** Menu alignment relative to the trigger. */
    menuAlign?: 'start' | 'end'
    /** Override the trigger's sizing/padding (merged last) — e.g. to match other navbar buttons. */
    triggerClassName?: string
}) {
    const { isMobile } = useScreenContext()
    const triggerSize = TRIGGER_SIZE[size]
    const baseConfig = isMobile ? MOBILE_DROPDOWN_ITEMS_CONFIG : DEFAULT_DROPDOWN_ITEMS_CONFIG
    const config = { ...baseConfig, ...configOverrides }
    const router = useRouter()
    const isOnManager = isManagerRoute(usePathname())
    const { closeModal } = useModalContext()
    const { openSettingsModal } = useSettingsModal()
    const organizationId = useOrganizationId()
    const { mutateAsync: createPortalSessionAsync } = useCreatePortalSessionMutation()
    const isSuperUser = useIsSuperUser()
    const { getRoute } = useBrandRoute()
    const { brand, brandSlug, setBrand } = useBrandContext()
    const { data: brands } = useGetBrandsQuery()
    const { openCreateBrandModal } = useCreateBrandFlowModal()
    const hasManagerAccess = useAccessControl(ManagerAccessControlRule)
    const enterManager = useEnterManagerMutation()
    const { capture: captureBrand, isStillCurrent: isStillCurrentBrand } = useCurrentBrandGuard()
    const showManagerEntry = hasManagerAccess && !isMobile && Boolean(brandSlug)

    const [isSwitching, setIsSwitching] = useState(false)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const [workspaceSearch, setWorkspaceSearch] = useState('')
    const [brandSearch, setBrandSearch] = useState('')
    const [brandToDelete, setBrandToDelete] = useState<IBrand | null>(null)
    const { deleteBrandFlow, isDeleting: isDeletingBrand } = useDeleteBrandFlow()
    const { openTutorialCenter } = useTutorialCenterModal()
    const { openReferralModal } = useReferralModal()
    const { enabled: captchaTestEnabled, trigger: triggerCaptchaTest } = useWafCaptchaTestAction()
    const isOnEmptyBrand = brand?.emptyBrand === true
    const { usage: brandsUsage } = useBillingFeatureLimit('brandsLimit')
    const canCreateBrand = useMemo(
        () => (brandsUsage ? !brandsUsage.isLimitReached : undefined) || !!isSuperUser,
        [brandsUsage, isSuperUser]
    )

    const { setActive } = useOrganizationList()
    const { organization: activeOrganization } = useOrganization()

    const { data: userOrganizations } = useOrganizationsQuery()
    const workspaceCount = userOrganizations?.length ?? (activeOrganization ? 1 : 0)
    const showWorkspaceSearch = workspaceCount > SWITCHER_OVERFLOW_THRESHOLD
    const normalizedWorkspaceSearch = showWorkspaceSearch ? workspaceSearch.trim().toLowerCase() : ''
    const filteredUserOrganizations = useMemo(() => {
        const organizations = userOrganizations ?? []

        if (!normalizedWorkspaceSearch) return organizations

        return organizations.filter((organization) => {
            const searchableText = `${organization.name} ${organization.slug ?? ''}`.toLowerCase()
            return searchableText.includes(normalizedWorkspaceSearch)
        })
    }, [normalizedWorkspaceSearch, userOrganizations])
    const hasWorkspaceSearch = normalizedWorkspaceSearch.length > 0
    const hasWorkspaceSearchResults = filteredUserOrganizations.length > 0
    const brandCount = brands?.length ?? 0
    const showBrandSearch = brandCount > SWITCHER_OVERFLOW_THRESHOLD
    const normalizedBrandSearch = showBrandSearch ? brandSearch.trim().toLowerCase() : ''
    const filteredBrands = useMemo(() => {
        const availableBrands = brands ?? []

        if (!normalizedBrandSearch) return availableBrands

        return availableBrands.filter((availableBrand) =>
            availableBrand.name.toLowerCase().includes(normalizedBrandSearch)
        )
    }, [brands, normalizedBrandSearch])
    const hasBrandSearch = normalizedBrandSearch.length > 0
    const hasBrandSearchResults = filteredBrands.length > 0

    const { signOut } = useSignOut({ redirectUrl: '/' })
    const currentUser = useUser()

    const user = {
        avatar: currentUser.user?.imageUrl ?? '',
        name: currentUser.user?.fullName ?? '',
        firstName: currentUser.user?.firstName ?? '',
        email: currentUser.user?.emailAddresses?.[0]?.emailAddress ?? '',
    }
    const triggerAvatar = user.avatar
    const triggerName = user.firstName || 'Workspace'
    const activeBrandName = brand?.name?.trim() ?? ''
    const hasActiveBrand = Boolean(activeBrandName)
    const hasResolvedActiveOrganization = Boolean(activeOrganization?.id)

    const handleSetActiveOrganization = useCallback(
        async (organization: IOrganization) => {
            if (!setActive) {
                return
            }
            if (organization.isMember) {
                setIsSwitching(true)
                const toastId = toast.loading(`Switching to ${organization.name}...`)
                await setActive({ organization: organization.id })
                router.push(UrlConfig['/routing'].getUrl())
                closeModal()
                toast.dismiss(toastId)
                setIsSwitching(false)
            }
        },
        [closeModal, router, setActive]
    )

    const { interactionLockRef } = useSidebarContext()

    const handleOpenChange = useCallback(
        (open: boolean) => {
            setIsDropdownOpen(open)
            if (!open) {
                setWorkspaceSearch('')
                setBrandSearch('')
            }
            interactionLockRef.current = open
        },
        [interactionLockRef]
    )

    const closeDropdown = useCallback(() => {
        setIsDropdownOpen(false)
        setWorkspaceSearch('')
        setBrandSearch('')
        interactionLockRef.current = false
    }, [interactionLockRef])

    const handleSignOut = useCallback(async () => {
        await signOut()
        closeModal()
    }, [closeModal, signOut])

    const handleOpenInvoices = useCallback(async () => {
        closeDropdown()
        if (!organizationId) {
            toast.error('Could not determine the active workspace')
            return
        }
        try {
            const { url } = await createPortalSessionAsync({ organizationId })
            if (url) window.open(url, '_blank', 'noopener,noreferrer')
        } catch {
            toast.error('Could not open billing portal')
        }
    }, [closeDropdown, organizationId, createPortalSessionAsync])

    const handleOpenManager = useCallback(async () => {
        closeDropdown()
        const capturedBrand = captureBrand()
        try {
            // Provision (or resolve) the comms manager, then open its dedicated route.
            // The mutation invalidates the manager-agent query, so /manager re-resolves it.
            // The door opens the manager in planning scope by default (until it has started work);
            // promoting to Full is a superuser action via the selector on the manager surface.
            await enterManager.mutateAsync()
            // The user may have switched brands while the provision ran — pushing
            // then would use the click-time getRoute and yank them back to the old
            // brand's manager page.
            if (!isStillCurrentBrand(capturedBrand)) return
            router.push(getRoute(Route.BRAND_MANAGER).getUrl())
        } catch {
            toast.error('Could not open the manager')
        }
    }, [closeDropdown, enterManager, router, getRoute, captureBrand, isStillCurrentBrand])

    const handleBackToPlatform = useCallback(() => {
        closeDropdown()
        router.push(getRoute(Route.BRAND_DASHBOARD).getUrl())
    }, [closeDropdown, router, getRoute])

    const handleSelectBrand = useCallback(
        (nextBrandUuid: string) => {
            closeDropdown()
            if (nextBrandUuid !== brand?.uuid) {
                setBrand(nextBrandUuid, 'push')
            }
        },
        [brand?.uuid, closeDropdown, setBrand]
    )

    const handleAddBrand = useCallback(() => {
        if (isOnEmptyBrand || canCreateBrand === undefined) return

        closeDropdown()
        if (canCreateBrand) {
            openCreateBrandModal()
            return
        }

        window.onSubscriptionError?.('USAGE_LIMIT_REACHED_IN_FREE_TEST', {
            billingDetails: { feature: 'brandsLimit' },
            source: 'create_brand_limit',
        })
    }, [canCreateBrand, closeDropdown, isOnEmptyBrand, openCreateBrandModal])

    if (isSwitching || (!hasActiveBrand && !triggerAvatar)) {
        return (
            <div className={cn('flex items-center gap-1.5', triggerSize.wrapper)}>
                {/* Reserve the resolved trigger's leading cell so the avatar doesn't shift or
                    resize when the brand lands. */}
                <div className={cn('flex shrink-0 items-center justify-center', triggerSize.cell)}>
                    <Skeleton className="size-6 rounded-lg" />
                </div>
                {!hideChevron && <Skeleton className="h-4 w-16 rounded" />}
            </div>
        )
    }

    return (
        <>
        <DropdownMenu open={isDropdownOpen} onOpenChange={handleOpenChange}>
            <DropdownMenuTrigger
                asChild
                className={cn(
                    'rounded-[8px] group hover:bg-black/5 px-2 cursor-pointer',
                    triggerSize.wrapper,
                    triggerClassName
                )}
            >
                <button
                    type="button"
                    aria-label={`${hasActiveBrand ? activeBrandName : triggerName} account menu`}
                    className="flex min-w-0 items-center gap-2 text-left"
                >
                    <span className={cn('flex shrink-0 items-center justify-center', triggerSize.cell)}>
                        {hasActiveBrand ? (
                            <BrandMonogram name={activeBrandName} size={triggerSize.monogram} />
                        ) : (
                            <Image
                                src={createImageUrl(triggerAvatar, {
                                    width: TRIGGER_AVATAR_SIZE,
                                    height: TRIGGER_AVATAR_SIZE,
                                })}
                                alt={triggerName}
                                width={TRIGGER_AVATAR_SIZE}
                                height={TRIGGER_AVATAR_SIZE}
                                className="size-6 shrink-0 rounded-lg object-fill group-hover:opacity-90"
                            />
                        )}
                    </span>

                    {!hideChevron && (
                        <>
                            <span className="flex min-w-0 flex-1 flex-col">
                                <span className="truncate text-[13px] font-medium leading-4 text-[#302E2C]">
                                    {hasActiveBrand ? activeBrandName : triggerName}
                                </span>
                                {hasActiveBrand && user.email && (
                                    <span className="truncate text-[11px] leading-[14px] text-[#817C77]">
                                        {user.email}
                                    </span>
                                )}
                            </span>
                            <ChevronsUpDown className="ml-auto size-3.5 shrink-0 text-[#817C77] transition-transform duration-150 group-data-[state=open]:rotate-180" />
                        </>
                    )}
                </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
                className={cn(
                    'bg-[#27272A] rounded-[8px] border border-black/10 outline outline-black text-white p-0',
                    size === 'lg' ? 'w-[280px]' : 'w-[240px]'
                )}
                align={menuAlign}
                side={menuSide}
                sideOffset={4}
            >
                <div className="no-scrollbar max-h-[90dvh] w-full overflow-x-hidden overflow-y-auto">
                    {!isMobile && (
                        <>
                            <DropdownMenuGroup>
                                <div className="gap-2 p-2 py-3 flex items-center">
                                    <Image
                                        src={createImageUrl(user.avatar, {
                                            width: 24,
                                            height: 24,
                                        })}
                                        alt={user.name}
                                        width={24}
                                        height={24}
                                        className="h-6 w-6 rounded-md object-fill"
                                    />
                                    <div className="grid flex-1 text-left text-[13px] leading-tight">
                                        <span className="truncate font-normal">{user.name}</span>
                                        <span className="truncate text-white/40">{user.email}</span>
                                    </div>
                                </div>
                            </DropdownMenuGroup>

                            <DropdownMenuSeparator className="bg-white/10 my-0" />
                        </>
                    )}

                    {isSuperUser && !isMobile && (
                        <>
                            <ActionBarDropdownItem
                                onClick={() => {
                                    closeDropdown()
                                    router.push(getRoute(Route.ADMIN_ALL_TOOLS).getUrl())
                                }}
                                Icon={ShieldCheck}
                            >
                                Admin
                            </ActionBarDropdownItem>
                            <ActionBarDropdownItem
                                onClick={() => {
                                    closeDropdown()
                                    router.push(getRoute(Route.ADMIN_SUPPORT).getUrl())
                                }}
                                Icon={Headset}
                            >
                                Support
                            </ActionBarDropdownItem>
                            <DropdownMenuSeparator className="bg-white/10 my-0" />
                        </>
                    )}

                    {showManagerEntry &&
                        (isOnManager ? (
                            <>
                                <ActionBarDropdownItem onClick={handleBackToPlatform} Icon={ArrowLeft}>
                                    Back to platform
                                </ActionBarDropdownItem>
                                <DropdownMenuSeparator className="bg-white/10 my-0" />
                            </>
                        ) : (
                            <>
                                <ActionBarDropdownItem onClick={() => handleOpenManager()} Icon={Sparkles}>
                                    Open Manager
                                </ActionBarDropdownItem>
                                <DropdownMenuSeparator className="bg-white/10 my-0" />
                            </>
                        ))}

                    {config.credits && hasResolvedActiveOrganization && (
                        <>
                            <ActionBarDropdownCredits onClose={closeDropdown} />
                            <DropdownMenuSeparator className="bg-white/10 my-0" />
                        </>
                    )}

                    {brand && (
                        <>
                            <DropdownMenuGroup>
                                <ActionBarDropdownLabel>Brands ({brandCount})</ActionBarDropdownLabel>

                                {showBrandSearch && (
                                    <AccountMenuSectionSearch
                                        ariaLabel="Search brands"
                                        placeholder="Search brands..."
                                        value={brandSearch}
                                        onValueChange={setBrandSearch}
                                    />
                                )}

                                <AccountMenuSwitcherList ariaLabel="Available brands">
                                    {filteredBrands.map((availableBrand) => {
                                        const isActiveBrand = availableBrand.uuid === brand.uuid
                                        // Hide row-level trash on the ONLY brand — the "at least one
                                        // brand" invariant is enforced up here since the backend has
                                        // no guard today.
                                        const canDeleteBrand = brandCount > 1

                                        return (
                                            <ActionBarDropdownItem
                                                key={availableBrand.uuid}
                                                className="group"
                                                aria-current={isActiveBrand ? 'true' : undefined}
                                                onClick={() => handleSelectBrand(availableBrand.uuid)}
                                            >
                                                <BrandMonogram name={availableBrand.name} size="sm" />
                                                <span className="min-w-0 flex-1 truncate text-[13px]">
                                                    {availableBrand.name}
                                                </span>
                                                <div className="ml-auto flex size-6 shrink-0 items-center justify-center">
                                                    {isActiveBrand && (
                                                        <Check
                                                            className={cn(
                                                                'size-4 stroke-white/75',
                                                                canDeleteBrand && 'group-hover:hidden'
                                                            )}
                                                        />
                                                    )}
                                                    {canDeleteBrand && (
                                                        <button
                                                            type="button"
                                                            aria-label={`Delete ${availableBrand.name}`}
                                                            className={cn(
                                                                'flex size-6 items-center justify-center rounded-md text-red-400 transition-colors hover:bg-red-500/10 hover:text-red-300 focus-visible:opacity-100',
                                                                isActiveBrand
                                                                    ? 'hidden group-hover:flex'
                                                                    : 'opacity-0 group-hover:opacity-100'
                                                            )}
                                                            onClick={(event) => {
                                                                event.preventDefault()
                                                                event.stopPropagation()
                                                                setBrandToDelete(availableBrand)
                                                            }}
                                                        >
                                                            <Trash className="size-3.5" />
                                                        </button>
                                                    )}
                                                </div>
                                            </ActionBarDropdownItem>
                                        )
                                    })}

                                    {hasBrandSearch && !hasBrandSearchResults && (
                                        <div className="px-3 py-3 text-[13px] leading-5 text-white/45">
                                            No brands found
                                        </div>
                                    )}
                                </AccountMenuSwitcherList>

                                <ActionBarDropdownItem
                                    disabled={isOnEmptyBrand || canCreateBrand === undefined}
                                    onClick={handleAddBrand}
                                >
                                    <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-white/[0.08]">
                                        <Plus className="size-4 stroke-white/75" />
                                    </span>
                                    <span className="flex min-w-0 flex-1 flex-col">
                                        <span>Add Brand</span>
                                        {isOnEmptyBrand && (
                                            <span className="truncate text-[11px] leading-4 text-white/40">
                                                {EMPTY_BRAND_CREATE_TOOLTIP}
                                            </span>
                                        )}
                                    </span>
                                </ActionBarDropdownItem>
                            </DropdownMenuGroup>

                            <DropdownMenuSeparator className="bg-white/10 my-0" />
                        </>
                    )}

                    {config.organizationSwitcher && (
                        <>
                            <DropdownMenuGroup>
                                <ActionBarDropdownLabel>Workspaces ({workspaceCount})</ActionBarDropdownLabel>

                                {showWorkspaceSearch && (
                                    <AccountMenuSectionSearch
                                        ariaLabel="Search workspaces"
                                        placeholder="Search workspaces..."
                                        value={workspaceSearch}
                                        onValueChange={setWorkspaceSearch}
                                    />
                                )}

                                <AccountMenuSwitcherList ariaLabel="Available workspaces">
                                    {filteredUserOrganizations.map((organization) => (
                                        <ActionBarDropdownItem
                                            key={organization.id}
                                            onClick={() => handleSetActiveOrganization(organization)}
                                        >
                                            <Image
                                                src={createImageUrl(organization.imageUrl, {
                                                    width: 24,
                                                    height: 24,
                                                })}
                                                alt={organization.name}
                                                width={24}
                                                height={24}
                                                className="h-6 w-6 rounded-md object-fill"
                                            />
                                            {organization.name}
                                        </ActionBarDropdownItem>
                                    ))}

                                    {hasWorkspaceSearch && !hasWorkspaceSearchResults && (
                                        <div className="px-3 py-3 text-[13px] leading-5 text-white/45">
                                            No workspaces found
                                        </div>
                                    )}
                                </AccountMenuSwitcherList>
                            </DropdownMenuGroup>

                            <DropdownMenuSeparator className="bg-white/10 my-0" />
                        </>
                    )}

                    {config.tutorials && (
                        <ActionBarDropdownItem
                            onClick={() => {
                                closeDropdown()
                                openTutorialCenter()
                            }}
                            Icon={BookOpen}
                        >
                            Tutorials
                        </ActionBarDropdownItem>
                    )}

                    {config.productNews && (
                        <ActionBarDropdownItem
                            onClick={() => {
                                window.open('https://docs.superscale.ai/changelog', '_blank', 'noopener,noreferrer')
                            }}
                            Icon={Newspaper}
                        >
                            Product News
                        </ActionBarDropdownItem>
                    )}

                    {config.inviteTeam && (
                        <ActionBarDropdownItem
                            disabled={!hasResolvedActiveOrganization}
                            onClick={() => {
                                openSettingsModal({ activeTab: 'organization', organizationPage: 'members' })
                            }}
                            Icon={Users}
                        >
                            Invite Team
                        </ActionBarDropdownItem>
                    )}

                    {config.referral && (
                        <ActionBarDropdownItem
                            onClick={() => {
                                closeDropdown()
                                openReferralModal()
                            }}
                            Icon={Gift}
                        >
                            Refer a Friend
                        </ActionBarDropdownItem>
                    )}

                    {config.billing && (
                        <ActionBarDropdownItem
                            disabled={!hasResolvedActiveOrganization}
                            onClick={() => {
                                openSettingsModal({ activeTab: 'plans', plansSource: 'account_menu_upgrade_plan' })
                            }}
                            Icon={ArrowUpCircle}
                        >
                            Upgrade Plan
                        </ActionBarDropdownItem>
                    )}

                    {config.invoices && (
                        <ActionBarDropdownItem
                            disabled={!hasResolvedActiveOrganization}
                            onClick={handleOpenInvoices}
                            Icon={Receipt}
                        >
                            Invoices
                        </ActionBarDropdownItem>
                    )}

                    {config.settings && (
                        <ActionBarDropdownItem onClick={() => openSettingsModal({ activeTab: 'user' })} Icon={Settings}>
                            Settings
                        </ActionBarDropdownItem>
                    )}

                    {captchaTestEnabled && (
                        <ActionBarDropdownItem
                            onClick={() => void triggerCaptchaTest().catch(() => undefined)}
                            Icon={ShieldCheck}
                        >
                            Test CAPTCHA
                        </ActionBarDropdownItem>
                    )}

                    {config.signOut && (
                        <>
                            {!isMobile && <DropdownMenuSeparator className="bg-white/10 my-0" />}

                            <ActionBarDropdownItem onClick={handleSignOut} Icon={LogOut}>
                                Sign out
                            </ActionBarDropdownItem>
                        </>
                    )}
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
        <DeleteBrandConfirmationModal
            open={!!brandToDelete}
            onOpenChange={(next) => {
                if (!next) setBrandToDelete(null)
            }}
            brandName={brandToDelete?.name ?? ''}
            isDeleting={isDeletingBrand}
            onConfirm={() => (brandToDelete ? deleteBrandFlow(brandToDelete) : undefined)}
        />
        </>
    )
}

const ActionBarDropdownLabel = ({ children }: { children: React.ReactNode }) => {
    return <DropdownMenuLabel className="text-[13px] text-white/50 font-normal py-1.5">{children}</DropdownMenuLabel>
}

const ActionBarDropdownItem = ({
    Icon,
    children,
    className,
    ...props
}: React.ComponentProps<typeof DropdownMenuItem> & { Icon?: LucideIcon }) => {
    return (
        <DropdownMenuItem
            className={cn(
                'flex items-center text-[13px] bg-black/0 hover:bg-black/10 focus:bg-black/10 data-highlighted:bg-white/10 text-white/95 hover:text-white focus:text-white data-highlighted:text-white px-2.5 py-2 cursor-default',
                className
            )}
            {...props}
        >
            {Icon && <Icon className="max-h-[14px] max-w-[14px] stroke-white/70" />}
            {children}
        </DropdownMenuItem>
    )
}
